import React, { useEffect, useState } from 'react';
import { formatMoney, formatNomenclatureName, formatNumber, formatUnit } from '../../utils/gazTextFormat';
import { resolveGazPriceDrafts } from './gazProposalManualPrices';
import { resolveGazCompactAlternative, gazCompactAlternativeKey } from './gazCompactAlternative';
import './gazProposal.css';

/**
 * Готовое КП прямо на странице — МСК Газобетон.
 *
 * На экране один расчёт — выбранный вариант со всей его логистикой. Второй
 * вариант не занимает половину экрана второй карточкой: он спрятан за одной
 * вторичной кнопкой и раскрывается предварительным просмотром.
 *
 * Порядок разговора с клиентом сохраняется:
 *   1. чем повезём — машины выбранного варианта;
 *   2. есть ли вариант дешевле и чего в нём не хватит;
 *   3. что именно он даёт — предварительный просмотр по нажатию;
 *   4. осознанный выбор — с подтверждением, а не одним кликом.
 * Ниже — готовый текст сообщения для выбранного варианта.
 *
 * Своих расчётов компонент не делает: варианты, машины, строки КП, суммы и текст
 * приходят готовыми одной моделью (priceModel). Правка цен здесь — только ввод:
 * поле отдаёт наверх ручную цену, а пересчитанные строки, итоги и текст приходят
 * обратно той же моделью.
 */

const COPY_FEEDBACK_MS = 2200;

/** Транспорт варианта: состав из модели машин, а если его нет — число машин. */
const transportTextOf = (option) => option.vehicleTypes || option.vehiclesText || null;

/** «31,875 м³» — запись объёма одна на все строки блока. */
const volumeText = (value) => `${formatNumber(value)} м³`;

/**
 * Подпись сегмента переключателя: «36 м³ · 2 машины · 239 000 ₽».
 *
 * Оба варианта названы полностью и рядом, поэтому сравнить их можно не открывая
 * ничего. Числа готовые — из самого варианта.
 */
const switchNoteOf = (option, volume) => [
  volumeText(volume),
  transportTextOf(option),
  `${formatMoney(option.total)} ₽`,
].filter(Boolean).join(' · ');

/** Строка редактора: «31,875 м³ ×» — количество показывается, но не правится. */
const lineQuantityText = (line) => `${formatNumber(line.quantity) ?? '0'} ${formatUnit(line.unit)}`;

/**
 * @param {object}   props
 * @param {Array}    props.options         — варианты КП (первый выбран по умолчанию)
 * @param {string}   props.mode            — выбранный вариант (состояние живёт выше)
 * @param {function} [props.onModeChange]  — сообщить выбранный вариант наверх
 * @param {function} [props.renderVehicles] — машины варианта внутрь его карточки
 * @param {object}   [props.priceModel]    — строки, суммы, итог и текст КП (готовые)
 * @param {object}   [props.priceOverrides] — уже применённые ручные цены
 * @param {function} [props.onPriceOverridesChange] — отдать ручные цены наверх
 */
const GazClientProposal = ({
  options,
  mode,
  onModeChange,
  renderVehicles,
  priceModel = null,
  priceOverrides = null,
  onPriceOverridesChange,
}) => {
  const list = Array.isArray(options) ? options : [];
  // Своего состояния выбора здесь нет: тот же вариант показывает верхняя сводка,
  // поэтому он живёт у общего родителя и приходит пропсом.
  const setMode = onModeChange;

  // Выбранный вариант мог исчезнуть: заказ стал полным, остаток пропал. Тогда
  // берётся первый доступный — он всегда «Весь заказ».
  const activeOption = list.find((option) => option.mode === mode) || list[0] || null;

  // Модель считает родитель — тем же вызовом, которым берёт итог для «К оплате»:
  // расхождения между верхней суммой и суммой в КП тогда быть не может.
  const model = priceModel;
  const generatedText = model?.text || '';

  // Набранное в поле — черновик: к расчёту и к сообщению клиенту оно не
  // применяется, пока менеджер не нажал «Сохранить цены». Так цена не уходит
  // клиенту наполовину набранной, а поле можно стереть и набрать заново.
  const [priceDrafts, setPriceDrafts] = useState({});

  // Выход из режима правки с несохранёнными черновиками: что сделать, если
  // менеджер подтвердит отмену. null — переспрашивать не о чем.
  const [pendingExit, setPendingExit] = useState(null);

  // Расчёт изменился — производство, состав, количество, адрес, вариант заказа
  // или разгрузка: набранное относится уже к другим строкам. Тот же приём
  // «состояние подстраивается под новые пропсы», что и у просмотра альтернативы.
  const baseKey = model?.baseKey || '';
  const [lastBaseKey, setLastBaseKey] = useState(baseKey);
  if (baseKey !== lastBaseKey) {
    setLastBaseKey(baseKey);
    setPriceDrafts({});
    setPendingExit(null);
  }

  // Выбранный вариант нужен и остальным путям отправки (заполнение сделки),
  // поэтому сообщаем его наверх существующим обработчиком.
  const activeMode = activeOption?.mode;
  useEffect(() => {
    if (activeMode && onModeChange) {
      onModeChange(activeMode);
    }
  }, [activeMode, onModeChange]);

  // Вариант дешевле: есть ли что предлагать и какими цифрами. Своего расчёта нет —
  // всё готовым приходит из вариантов.
  const alternative = resolveGazCompactAlternative(list);

  // Шаг просмотра альтернативы: null — закрыт, 'preview' — открыт, 'confirm' —
  // спрашиваем подтверждение. Это состояние показа, а не расчёта: выбранный
  // вариант по-прежнему живёт выше и меняется существующим обработчиком.
  const [alternativeStep, setAlternativeStep] = useState(null);

  // Товар, производство или расчёт изменились — устаревший просмотр закрывается,
  // тем же приёмом «состояние подстраивается под новые пропсы», что и черновик.
  // Выбранный вариант входит в отпечаток, поэтому после подтверждения просмотр
  // закрывается сам.
  const alternativeKey = gazCompactAlternativeKey(alternative, activeOption?.mode);
  const [lastAlternativeKey, setLastAlternativeKey] = useState(alternativeKey);
  if (alternativeKey !== lastAlternativeKey) {
    setLastAlternativeKey(alternativeKey);
    setAlternativeStep(null);
  }

  const [copyState, setCopyState] = useState(null);
  // По умолчанию готовое сообщение показывается предпросмотром: редактор цен —
  // отдельное действие, а не постоянный рабочий инструмент на экране.
  const [isEditing, setIsEditing] = useState(false);

  // Подтверждение копирования держится недолго и снимается само.
  useEffect(() => {
    if (copyState !== 'ok') {
      return undefined;
    }
    const timer = setTimeout(() => setCopyState(null), COPY_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copyState]);

  if (!activeOption || !generatedText) {
    return null;
  }

  const hasManualPrices = Boolean(model?.hasManualPrices);

  // Применённые цены строк — то, с чем сравнивается набранное: изменением
  // считается только то, что отличается от стоящего сейчас в расчёте.
  const appliedPrices = {};
  for (const line of [...(model?.materialLines || []), ...(model?.deliverySectionLines || [])]) {
    appliedPrices[line.id] = line.pricePerOne;
  }

  // Что негодно, что менеджер изменил и можно ли сохранять — одним разбором,
  // тем же, что накладывает цену на строку.
  const drafts = resolveGazPriceDrafts(priceDrafts, appliedPrices);

  /** Ввод цены — только в черновик: расчёт и сообщение клиенту он не трогает. */
  const handlePriceInput = (id, raw) => {
    setPriceDrafts({ ...priceDrafts, [id]: raw });
  };

  /**
   * «Сохранить цены» — единственный момент, когда набранное становится ценой.
   * Дальше всё считает существующая модель КП: суммы строк, итоги материалов и
   * доставки, общий итог с прежним округлением и текст сообщения.
   */
  const handleSavePrices = () => {
    if (!drafts.canSave) {
      return;
    }
    if (typeof onPriceOverridesChange === 'function') {
      onPriceOverridesChange({ ...(priceOverrides || {}), ...drafts.values });
    }
    setPriceDrafts({});
    setPendingExit(null);
    setIsEditing(false);
    setCopyState(null);
  };

  /**
   * Уход из режима правки. Набранное и не сохранённое молча не пропадает:
   * сначала переспрос, и только по согласию значения возвращаются к тем, что
   * были до открытия режима (черновики к расчёту не применялись — значит, это
   * ровно те цены, что стоят сейчас).
   */
  const requestExit = (exit) => {
    if (drafts.isDirty) {
      setPendingExit(exit);
      return;
    }
    setPriceDrafts({});
    setIsEditing(false);
    if (exit?.kind === 'variant') {
      runVariantExit(exit.mode);
    }
  };

  const discardPriceDrafts = () => {
    const exit = pendingExit;
    setPriceDrafts({});
    setPendingExit(null);
    setIsEditing(false);
    if (exit?.kind === 'variant') {
      runVariantExit(exit.mode);
    }
  };

  const handleResetPrices = () => {
    setPriceDrafts({});
    setPendingExit(null);
    setCopyState(null);
    if (typeof onPriceOverridesChange === 'function') {
      onPriceOverridesChange({});
    }
  };

  /**
   * Enter сохраняет, но только когда сохранять есть что и всё разобрано; Escape
   * выходит из режима — через тот же переспрос, что и кнопка.
   */
  const handlePriceKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSavePrices();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      requestExit({ kind: 'close' });
    }
  };

  // Копируется последнее сохранённое сообщение: черновики в него не попадают,
  // потому что к модели КП они не применялись.
  const handleCopy = async () => {
    const text = generatedText;
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('ok');
    } catch (error) {
      // Запасной путь для окружений без доступа к буферу (например, внутри Bitrix).
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.left = '-9999px';
      document.body.appendChild(area);
      area.focus({ preventScroll: true });
      area.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(area);
      setCopyState(copied ? 'ok' : 'fail');
    }
  };

  /**
   * Раздел редактора цен: строки одного вида и их цена. Количество, объём и число
   * рейсов только показываются — правится единственное поле, цена.
   */
  const renderPriceGroup = (title, lines, priceHint) => {
    if (!Array.isArray(lines) || lines.length === 0) {
      return null;
    }
    return (
      <div className="gaz-kp__price-group">
        <div className="gaz-kp__price-group-title">{title}</div>
        {lines.map((line) => {
          const draft = priceDrafts[line.id];
          const value = draft === undefined ? (formatNumber(line.pricePerOne) ?? '') : draft;
          const isInvalid = drafts.invalidIds.includes(line.id);
          const isPending = drafts.changedIds.includes(line.id);
          return (
            <div className="gaz-kp__price-row" key={line.id}>
              <div className="gaz-kp__price-name">
                {formatNomenclatureName(line.name)}
                {line.isManual && <span className="gaz-kp__price-manual">изменена</span>}
              </div>
              <div className="gaz-kp__price-fields">
                <span className="gaz-kp__price-qty">{lineQuantityText(line)} ×</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`gaz-kp__price-input${isInvalid ? ' is-invalid' : ''}${isPending ? ' is-pending' : ''}`}
                  value={value}
                  onChange={(event) => handlePriceInput(line.id, event.target.value)}
                  onKeyDown={handlePriceKeyDown}
                  aria-label={`${line.name} — ${priceHint}`}
                  aria-invalid={isInvalid}
                />
                <span className="gaz-kp__price-unit">₽</span>
                {/* Сумма строки — от сохранённой цены: черновик её ещё не менял. */}
                <span className="gaz-kp__price-sum">= {formatMoney(line.sum)} ₽</span>
              </div>
              {isInvalid && (
                <p className="gaz-kp__price-error" role="alert">
                  Цена — число больше нуля
                </p>
              )}
              {!isInvalid && isPending && (
                <p className="gaz-kp__price-pending">Не сохранено</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Выбор варианта идёт существующим обработчиком — тем же, что и раньше стоял
   * за карточками. Своего состояния расчёта здесь не заводится.
   */
  const pick = (option) => {
    if (option && option.mode !== activeOption.mode && typeof setMode === 'function') {
      setMode(option.mode);
    }
  };

  const isPartialPicked = Boolean(alternative) && activeOption.mode === alternative.partial.mode;

  /**
   * Действие переключателя вариантов — ровно то же, что и было за кнопками.
   * Уход в неполную поставку по-прежнему идёт через свой переспрос: это другой
   * вопрос, чем несохранённые цены, и подменять его нельзя.
   */
  const runVariantExit = (mode) => {
    if (!alternative) {
      return;
    }
    if (mode === alternative.partial.mode) {
      if (!isPartialPicked) {
        setAlternativeStep('confirm');
      }
      return;
    }
    setAlternativeStep(null);
    pick(alternative.whole);
  };

  /** Переключение варианта — тоже выход из режима правки, с тем же переспросом. */
  const requestVariant = (mode) => {
    if (isEditing) {
      requestExit({ kind: 'variant', mode });
      return;
    }
    runVariantExit(mode);
  };

  return (
    <section className="gaz-kp" aria-label="Коммерческое предложение">
      {/* Переключатель вариантов погрузки. Два сегмента рядом, у каждого свои
          объём, транспорт и цена: сравнение видно сразу, без раскрытия. Стоит
          выше машин — сначала выбираем, чем везём, потом смотрим чем повезли. */}
      {alternative && (
        <div className="gaz-kp__switch" role="group" aria-label="Вариант погрузки">
          <button
            type="button"
            className={`gaz-kp__switch-btn${isPartialPicked ? '' : ' is-active'}`}
            aria-pressed={!isPartialPicked}
            onClick={() => requestVariant(alternative.whole.mode)}
          >
            <span className="gaz-kp__switch-title">Весь объём</span>
            <span className="gaz-kp__switch-note">
              {switchNoteOf(alternative.whole, alternative.requestedVolume)}
            </span>
          </button>
          {/* Возврат к полному заказу безопасен и идёт сразу. Уход в неполную
              поставку — нет: клиент недополучит товар, поэтому сначала переспрос. */}
          <button
            type="button"
            className={`gaz-kp__switch-btn${isPartialPicked ? ' is-active' : ''}`}
            aria-pressed={isPartialPicked}
            onClick={() => requestVariant(alternative.partial.mode)}
          >
            <span className="gaz-kp__switch-title">Только полные машины</span>
            <span className="gaz-kp__switch-note">
              {switchNoteOf(alternative.partial, alternative.partialVolume)}
            </span>
          </button>
        </div>
      )}

      {/* Что даёт второй вариант — одной строкой, до того как менеджер его нажал. */}
      {alternative && !isPartialPicked && alternativeStep === null && (
        <p className="gaz-kp__switch-hint">
          Только полные машины — дешевле на {formatMoney(alternative.savings)} ₽,
          {' но не хватит '}{volumeText(alternative.shortfall)}
        </p>
      )}

      {/* Выбрана неполная поставка — недопоставка остаётся на виду. */}
      {alternative && isPartialPicked && (
        <p className="gaz-kp__switch-risk">
          Клиент недополучит {volumeText(alternative.shortfall)}
        </p>
      )}

      {/* Переспрос перед уходом в неполную поставку: те же цифры, что и в
          переключателе, и явное согласие вместо одного клика. */}
      {alternative && alternativeStep === 'confirm' && (
        <div className="gaz-kp__confirm">
          <p className="gaz-kp__confirm-text">
            Клиент получит {volumeText(alternative.partialVolume)} из{' '}
            {volumeText(alternative.requestedVolume)}. Не хватит{' '}
            {volumeText(alternative.shortfall)}. Выбрать этот вариант?
          </p>
          <div className="gaz-kp__confirm-actions">
            <button
              type="button"
              className="gaz-kp__confirm-btn gaz-kp__confirm-btn--primary"
              onClick={() => pick(alternative.partial)}
            >
              Выбрать этот вариант
            </button>
            <button
              type="button"
              className="gaz-kp__confirm-btn"
              onClick={() => setAlternativeStep(null)}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Машины выбранного варианта — под переключателем, во всю ширину. */}
      {renderVehicles && (
        <div className="gaz-kp__details">{renderVehicles(activeOption)}</div>
      )}

      {/* Главное действие экрана — сразу после выбора варианта, до поля с текстом. */}
      <div className="gaz-kp__preview">
        <div className="gaz-kp__preview-head">
          <h3 className="gaz-kp__preview-title">Сообщение клиенту</h3>
          {hasManualPrices && (
            <span className="gaz-kp__price-flag">Цена изменена вручную</span>
          )}
          {/* Сброс показывается, только когда есть что сбрасывать — сохранённые
              ручные цены. Форма и размер те же, что у основных действий, но
              кнопка вторичная: случайный сброс не должен выглядеть главным. */}
          {hasManualPrices && (
            <button
              type="button"
              className="gaz-kp__action gaz-kp__action--secondary"
              onClick={handleResetPrices}
            >
              Сбросить цены
            </button>
          )}
          {isEditing ? (
            <>
              {/* Выход без сохранения. Escape делает то же самое, но он живёт на
                  самих полях цены: зашёл посмотреть, ничего не менял и никуда не
                  ткнул — «Сохранить цены» неактивна, и выйти мышью было нечем. */}
              <button
                type="button"
                className="gaz-kp__action gaz-kp__action--secondary"
                onClick={() => requestExit({ kind: 'close' })}
              >
                Отмена
              </button>
              {/* Завершение правки — заметное действие: пока цены не сохранены, они
                  никуда не применены. Кнопка доступна только когда есть что
                  сохранять и всё разобрано. */}
              <button
                type="button"
                className="gaz-kp__action gaz-kp__action--primary"
                onClick={handleSavePrices}
                disabled={!drafts.canSave}
              >
                Сохранить цены
              </button>
            </>
          ) : (
            <button
              type="button"
              className="gaz-kp__action gaz-kp__action--primary"
              onClick={() => setIsEditing(true)}
            >
              Изменить цены
            </button>
          )}
        </div>

        {/* Переспрос перед потерей набранного: молча черновики не пропадают. */}
        {pendingExit && (
          <div className="gaz-kp__confirm">
            <p className="gaz-kp__confirm-text">
              Изменения цен не сохранены. Отменить изменения?
            </p>
            <div className="gaz-kp__confirm-actions">
              <button
                type="button"
                className="gaz-kp__confirm-btn gaz-kp__confirm-btn--primary"
                onClick={discardPriceDrafts}
              >
                Отменить изменения
              </button>
              <button
                type="button"
                className="gaz-kp__confirm-btn"
                onClick={() => setPendingExit(null)}
              >
                Продолжить правку
              </button>
            </div>
          </div>
        )}

        {/* Редактирование цен — структурное: у материала цена за единицу, у строки
            доставки цена одного рейса, у разгрузки её цена. Количество, объём и
            число рейсов остаются от расчёта: их менеджер не переназначает. Суммы
            строк, итоги разделов и итог к оплате приходят уже пересчитанными. */}
        {isEditing && model && (
          <div className="gaz-kp__prices">
            {renderPriceGroup('Материалы', model.materialLines, 'цена за единицу')}
            {model.materialLines.length > 0 && (
              <div className="gaz-kp__price-total">
                Итого материалы<b>{formatMoney(model.materialsTotal)} ₽</b>
              </div>
            )}
            {renderPriceGroup('Доставка', model.deliveryLines, 'цена одного рейса')}
            {renderPriceGroup('Разгрузка', model.unloadingLines, 'цена разгрузки')}
            {model.deliverySectionLines.length > 0 && (
              <div className="gaz-kp__price-total">
                Итого доставка<b>{formatMoney(model.deliveryTotal)} ₽</b>
              </div>
            )}
            <div className="gaz-kp__price-total gaz-kp__price-total--grand">
              {model.cashApplies ? 'Итого к оплате наличными' : 'Итого к оплате'}
              <b>{formatMoney(model.payable)} ₽</b>
            </div>
          </div>
        )}

        {/* Готовое сообщение видно всегда — и во время правки цен тоже: менеджер
            сразу видит, что уйдёт клиенту. Это read-only блок, а не поле ввода:
            текст выделяется и переносится, но ни рамки, ни ручки размера, ни
            поведения textarea у него нет. */}
        <div className="gaz-kp__text gaz-kp__text--preview">{generatedText}</div>

        {/* Единственное основное действие экрана — копирование текущего текста.
            Копируется последнее сохранённое сообщение: набранные, но не
            сохранённые цены в него не попадают. */}
        <button
          type="button"
          className={`gaz-kp__copy${copyState === 'ok' ? ' is-done' : ''}`}
          onClick={handleCopy}
        >
          {copyState === 'ok' ? '✓ Сообщение скопировано' : 'Скопировать'}
        </button>
        {isEditing && drafts.isDirty && (
          <p className="gaz-kp__copy-note">
            Копируется сохранённое сообщение — несохранённых цен в нём нет.
          </p>
        )}
        <span className="gaz-kp__copy-live" role="status" aria-live="polite">
          {copyState === 'ok' ? 'Сообщение скопировано' : ''}
        </span>
        {copyState === 'fail' && (
          <p className="gaz-kp__copy-error" role="alert">
            Не удалось скопировать — выделите текст выше и скопируйте вручную.
          </p>
        )}
      </div>
    </section>
  );
};

export default GazClientProposal;
